import { Component, OnInit, AfterViewInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { Input, Output, EventEmitter } from '@angular/core';

import { languageService } from '@unpispas/upp-base';
import { viewService } from '@unpispas/upp-base';
import { i18nSupport } from '@unpispas/upp-data';
import { PlaceView } from '@unpispas/upp-data';
import { PreselectView } from '@unpispas/upp-data';
import { Product } from '@unpispas/upp-data';
import { ProductView } from '@unpispas/upp-data';
import { UppInputComponent } from '@unpispas/upp-wdgt';

@Component({
    selector: 'upp-product-match',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './upp-match.html',
    styleUrls: ['./upp-match.scss'],
})
export class UppProductMatchComponent extends i18nSupport implements OnInit, AfterViewInit, OnDestroy {
    readonly Product = Product;

    @Input() place: PlaceView | null = null;
    @Input() initialValue = '';

    @Output() OnDismiss = new EventEmitter<ProductView | PreselectView | null>();

    @ViewChild('input', { static: false }) private _input: UppInputComponent | null = null;
    @ViewChild('scrbox', { static: false }) private _scrbox: ElementRef | null = null;

    private _onScroll = (): void => {
        this.OnScroll();
    };

    private _searchValue = '';
    private _matches: Array<ProductView | PreselectView> = [];
    private _matchKeyByItem = new Map<ProductView | PreselectView, string>();
    private _exactMatch: ProductView | PreselectView | null = null;

    constructor(
        private lang: languageService,
        private change: ChangeDetectorRef,
        public view: viewService
    ){
        super(lang, change);
    }

    ngOnInit(): void {
        this.SearchValue = this.initialValue;
    }

    ngAfterViewInit(): void {
        this._input?.SetFocus?.();
        this._BindScroll();
    }

    ngOnDestroy(): void {
        this._UnbindScroll();
        super.OnDestroy();
    }

    get SearchValue(): string {
        return this._searchValue;
    }

    set SearchValue(value: string){
        const _value = String(value ?? '');
        if (_value === this._searchValue){
            return;
        }

        this._searchValue = _value;
        this._RefreshMatches();
    }

    get Matches(): Array<ProductView | PreselectView> {
        return this._matches;
    }

    get ExactMatch(): ProductView | PreselectView | null {
        return this._exactMatch;
    }

    get CanAccept(): boolean {
        return this._matches.length > 1 && this._exactMatch !== null;
    }

    ItemKey(item: ProductView | PreselectView): string {
        return this._matchKeyByItem.get(item) ?? String(item.code ?? '').trim();
    }

    ItemPhoto(item: ProductView | PreselectView): string | null {
        if (item.ViewOf(Product)){
            return (item as ProductView).photo;
        }

        return (item as PreselectView).product?.photo ?? null;
    }

    ItemPrice(item: ProductView | PreselectView): { int: string; dec: string } | null {
        if (item.ViewOf(Product)){
            const _product = item as ProductView;
            if (_product.IsMarket){
                return null;
            }

            return _product.MinPrice();
        }

        const _preselect = item as PreselectView;
        if (!_preselect.priceinfo){
            return null;
        }

        return _preselect.priceinfo;
    }

    OnAccept(): void {
        if (!this.CanAccept || !this.ExactMatch){
            return;
        }

        this.OnSelectItem(this.ExactMatch);
    }

    OnNumPad(key: string | number): void {
        this._input?.OnKeyPress(String(key));
    }

    OnScroll(): void {
        const _list = this._ScrollElement();
        const _box = this._scrbox?.nativeElement;
        if (!_list || !_box){
            return;
        }

        const _isTop = _list.scrollTop === 0;
        const _isBot = _list.scrollHeight - _list.scrollTop === _list.offsetHeight;

        if (_isTop){
            _box.classList.add('top');
        }
        else {
            _box.classList.remove('top');
        }

        if (_isBot){
            _box.classList.add('bot');
        }
        else {
            _box.classList.remove('bot');
        }
    }

    OnSelectItem(item: ProductView | PreselectView): void {
        this.OnDismiss.emit(item);
    }

    OnClose(): void {
        this.OnDismiss.emit(null);
    }

    private _ScrollElement(): HTMLElement | null {
        return this._scrbox?.nativeElement?.querySelector('.upp-scrollable-content') ?? null;
    }

    private _BindScroll(): void {
        const _list = this._ScrollElement();
        if (!_list){
            return;
        }

        _list.addEventListener('scroll', this._onScroll);
        this.OnScroll();
    }

    private _UnbindScroll(): void {
        this._ScrollElement()?.removeEventListener('scroll', this._onScroll);
    }

    private _RefreshMatches(): void {
        if (!this.place){
            this._matches = [];
            this._matchKeyByItem = new Map();
            this._exactMatch = null;
            this.change.markForCheck();
            return;
        }

        const _pattern = this._searchValue.trim();
        if (!_pattern){
            this._matches = [];
            this._matchKeyByItem = new Map();
            this._exactMatch = null;
            this.change.markForCheck();
            return;
        }

        const _merged = this._MergeMatches(_pattern);
        this._matches = _merged.items;
        this._matchKeyByItem = _merged.keys;

        const _exact = this._matches.filter((item) => this.ItemKey(item) === _pattern);
        this._exactMatch = _exact.length === 1 ? _exact[0] : null;

        this.change.markForCheck();
        setTimeout(() => this.OnScroll(), 0);

        if (this._matches.length === 1){
            setTimeout(() => void this.OnSelectItem(this._matches[0]), 0);
        }
    }

    private _MergeMatches(pattern: string): {
        items: Array<ProductView | PreselectView>;
        keys: Map<ProductView | PreselectView, string>;
    } {
        const _keys = new Map<ProductView | PreselectView, string>();
        const _items: Array<ProductView | PreselectView> = [];
        const _place = this.place;
        if (!_place){
            return { items: _items, keys: _keys };
        }

        for (const _item of _place.doPrdCode(pattern)){
            if (_keys.has(_item)){
                continue;
            }

            _keys.set(_item, String(_item.code ?? '').trim());
            _items.push(_item);
        }

        for (const _item of _place.doBarCode(pattern)){
            if (_keys.has(_item)){
                continue;
            }

            _keys.set(_item, String(_item.barcode ?? '').trim());
            _items.push(_item);
        }

        return {
            items: _items,
            keys: _keys,
        };
    }
}
